/**
 * Asset/annotation filter query helpers.
 *
 * Wire format:
 * - Legacy: [{type, value}, ...]  (implicit AND)
 * - Grouped: { op: 'AND'|'OR', groups: [[{type, value}, ...], ...] }
 *
 * Filters within a group are always AND'd. op combines groups.
 */

export function emptyFilterQuery() {
  return { op: 'AND', groups: [[]] };
}

export function normalizeFilterQuery(input) {
  if (input == null || input === false || input === '') {
    return emptyFilterQuery();
  }
  if (typeof input === 'string') {
    try {
      return normalizeFilterQuery(JSON.parse(input));
    } catch (e) {
      return emptyFilterQuery();
    }
  }
  if (Array.isArray(input)) {
    // Train-config style [[...],[...]] is out of scope; treat as flat if items look like filters.
    if (input.length > 0 && Array.isArray(input[0])) {
      return {
        op: 'OR',
        groups: input.map((g) => (Array.isArray(g) ? g.slice() : [])),
      };
    }
    return { op: 'AND', groups: [input.slice()] };
  }
  if (typeof input === 'object') {
    const op = String(input.op || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND';
    let groups = input.groups;
    if (!Array.isArray(groups) || groups.length === 0) {
      groups = [[]];
    }
    groups = groups.map((g) => (Array.isArray(g) ? g.slice() : []));
    return { op, groups };
  }
  return emptyFilterQuery();
}

/** JSON value for FormData `filters` / job descriptions. */
export function toApiFilters(query) {
  const fq = normalizeFilterQuery(query);
  if (fq.op === 'AND' && fq.groups.length === 1) {
    return fq.groups[0];
  }
  return { op: fq.op, groups: fq.groups };
}

export function flatFilters(query) {
  const fq = normalizeFilterQuery(query);
  const out = [];
  for (const g of fq.groups) {
    for (const f of g) {
      out.push(f);
    }
  }
  return out;
}

export function findFiltersByType(query, type) {
  const want = Number(type);
  return flatFilters(query).filter((f) => Number(f && f.type) === want);
}

export function filterQueryHasTypeValue(query, type, value) {
  return findFiltersByType(query, type).some((f) => f.value === value);
}
